import { useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { message } from 'antd'

interface JobStatusUpdate {
  jobId: string
  status: string
  job: any
  timestamp: string
  notes?: string
}

interface PaymentNotification {
  type: 'payment_success'
  invoiceId: string
  amount: number
  message: string
  timestamp: string
}

interface InvoiceNotification {
  type: 'invoice_created'
  invoice: {
    id: string
    invoiceNumber: string
    totalAmount: number
    dueAt: string
  }
  message: string
  timestamp: string
}

interface UseWebSocketOptions {
  onJobStatusUpdate?: (update: JobStatusUpdate) => void
  onPaymentNotification?: (notification: PaymentNotification) => void
  onInvoiceNotification?: (notification: InvoiceNotification) => void
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const socketRef = useRef<Socket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Disable WebSocket in production for now to avoid connection issues
    // TODO: Re-enable when WebSocket server is properly configured
    console.log('🔌 WebSocket temporarily disabled to prevent connection issues')
    return
    
    const connectWebSocket = async () => {
      // Clear any existing reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }

      // Get authentication token from localStorage
      const token = localStorage.getItem('customerToken')
      if (!token) {
        console.log('🔌 No authentication token found, skipping WebSocket connection')
        setConnectionError('No authentication token found')
        return
      }

      try {
        // Connect to WebSocket server
        const { env } = await import('../config/environment')
        const serverUrl = env.wsBaseUrl

        console.log('🔌 Connecting to WebSocket server:', serverUrl)

        // Disconnect existing socket if any
        if (socketRef.current) {
          socketRef.current.disconnect()
        }

        const socket = io(serverUrl, {
          auth: {
            token
          },
          transports: ['websocket', 'polling'],
          timeout: 15000,
          reconnection: false, // Disable automatic reconnection to prevent loops
          forceNew: true
        })

        socketRef.current = socket

        // Connection event handlers
        socket.on('connect', () => {
          console.log('✅ Connected to WebSocket server')
          setIsConnected(true)
          setConnectionError(null)
        })

        socket.on('disconnect', (reason) => {
          console.log('❌ Disconnected from WebSocket server:', reason)
          setIsConnected(false)
          
          // Only attempt reconnect for certain disconnect reasons
          if (reason === 'io server disconnect' || reason === 'transport close') {
            console.log('🔄 Scheduling reconnection in 5 seconds...')
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket()
            }, 5000)
          }
        })

        socket.on('connect_error', (error) => {
          console.error('❌ WebSocket connection error:', error)
          setConnectionError(error.message)
          setIsConnected(false)
          
          // Don't attempt reconnect on connection errors to avoid spam
        })

        socket.on('auth_error', (error) => {
          console.error('❌ WebSocket authentication error:', error)
          setConnectionError(error.message)
          setIsConnected(false)
          
          // Don't attempt reconnect on auth errors
        })

        // Business event handlers
        socket.on('job_status_updated', (update: JobStatusUpdate) => {
          console.log('📱 Received job status update:', update)
          
          // Show notification to user
          message.info(`Job "${update.job.title}" status updated to ${update.status}`)
          
          // Call callback if provided
          if (options.onJobStatusUpdate) {
            options.onJobStatusUpdate(update)
          }
        })

        socket.on('payment_notification', (notification: PaymentNotification) => {
          console.log('💳 Received payment notification:', notification)
          
          // Show success message
          message.success(notification.message)
          
          // Call callback if provided
          if (options.onPaymentNotification) {
            options.onPaymentNotification(notification)
          }
        })

        socket.on('invoice_notification', (notification: InvoiceNotification) => {
          console.log('📄 Received invoice notification:', notification)
          
          // Show info message
          message.info(notification.message)
          
          // Call callback if provided
          if (options.onInvoiceNotification) {
            options.onInvoiceNotification(notification)
          }
        })

      } catch (error) {
        console.error('❌ Failed to initialize WebSocket:', error)
        setConnectionError('Failed to initialize WebSocket connection')
      }
    }

    // Only connect if we have callbacks (indicating the component actually needs WebSocket)
    if (options.onJobStatusUpdate || options.onPaymentNotification || options.onInvoiceNotification) {
      connectWebSocket()
    }

    // Cleanup on unmount
    return () => {
      console.log('🔌 Cleaning up WebSocket connection')
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, []) // Remove dependencies to prevent reconnection loops

  return {
    isConnected,
    connectionError,
    socket: socketRef.current
  }
}
